/**
 * Document editing + versioning state over the Phase 7 REST API.
 *
 * Owns one document's editing lifecycle for the Documents tab: it loads the
 * document handle + current committed version + the calling provider's
 * autosaved draft, debounces server-side autosave of edits (distinct from an
 * explicit commit), commits a new immutable version (surfacing the 409 rebase
 * conflict), and lists versions + fetches an old→new section diff for the
 * DiffView. All clinical content lives in component state only; nothing is
 * logged (HIPAA).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import { timeOperation } from "../utils/perfMetrics";
import type {
  BlueprintDocument,
  BlueprintDocumentResponse,
  BlueprintVersion,
  DocumentDraft,
  VersionDiff,
  VersionSummary,
} from "../types";

const DOCUMENTS_ENDPOINT = "/api/documents";

/** How long after the last keystroke to autosave a draft (ms). */
export const AUTOSAVE_DEBOUNCE_MS = 800;

/** The lifecycle status of a document load. */
export type DocumentStatus = "loading" | "loaded" | "error";

/** The autosave indicator state shown in the editor. */
export type SaveState = "idle" | "unsaved" | "saving" | "saved";

/** The outcome of an explicit commit attempt. */
export type CommitResult = "ok" | "conflict" | "error";

/**
 * The outcome of a restore attempt.
 *
 * `"approval"` means the backend schema-lock gate refused a section-reshaping
 * Blueprint restore (HTTP 422); the caller must obtain explicit provider
 * approval and retry with `providerApproved = true`.
 */
export type RestoreResult = "ok" | "approval" | "error";

/** The result surface returned by {@link useDocumentVersions}. */
export interface UseDocumentVersionsResult {
  /** The current load status. */
  status: DocumentStatus;
  /**
   * True while a same-document refresh (post-commit/restore reload) is
   * in flight. Distinct from `status === "loading"`, which is reserved for
   * the initial mount or a document switch (a new `documentId`, which also
   * covers a patient switch — the caller always passes a different document
   * id) — a refresh must not
   * blank the already-mounted editor (see {@link reload}), only surface a
   * subtle, non-destructive indicator.
   */
  isRefreshing: boolean;
  /** The document handle, once loaded. */
  document: BlueprintDocument | null;
  /** The current committed version, if any. */
  version: BlueprintVersion | null;
  /**
   * Whether the document was generated from Blueprint cards in ANY version
   * (backend-computed `has_card_provenance`). Gates "Sync with Blueprint" — a
   * normal edit-commit drops `source_card_ids` from the current version, so the
   * current version alone is not a reliable signal; this stays true across the
   * derivative's whole life so the button survives a post-generation edit.
   */
  hasCardProvenance: boolean;
  /**
   * `{ source id: title }` for every source the current version cites, so the
   * editor's inline citations render a human source label rather than the
   * neutral fallback. Empty when the document cites nothing.
   */
  sourceTitles: Record<string, string>;
  /**
   * `{ card id: name }` for the patient's Blueprint cards, so an inference
   * marker can name the cards it was drawn from rather than showing a raw id.
   */
  cardTitles: Record<string, string>;
  /** The version history (metadata only, oldest first). */
  versions: VersionSummary[];
  /** The live editor content (section-keyed markdown). */
  content: Record<string, string>;
  /**
   * The content that an explicit commit would persist: the live editor
   * {@link content} overlaid onto the committed version's content, so sections
   * that exist in the committed version but are not surfaced as editable
   * cards (notably the blueprint `metadata` block) are preserved rather than
   * dropped. A plain content edit therefore keeps the committed section *set*
   * intact, so the schema-lock approval gate is not spuriously tripped.
   * Sections deleted via {@link UseDocumentVersionsResult.deleteSection} are
   * subtracted *after* the overlay, so the reduced section set is what the
   * approval gate sees and what a commit persists.
   */
  commitContent: Record<string, string>;
  /** The autosave indicator state. */
  saveState: SaveState;
  /** Set to true after a commit hit a 409 (stale base version). */
  conflict: boolean;
  /** Whether the active draft was created against an older version than current (zero-wiped-drafts preservation). */
  draftConflict: boolean;
  /** The base_version the preserved draft was written against, if draftConflict is true. */
  draftBaseVersion: number | null;
  /** A human-readable commit error (non-409 failure), or null when none. */
  commitError: string | null;
  /** Update one section's markdown (schedules a debounced autosave). */
  editSection: (section: string, markdown: string) => void;
  /**
   * Remove a section from the working content (schedules a debounced autosave).
   *
   * Removing a section key is a structural change to the section *set*. The
   * removed key is also recorded so the commit overlay (which restores every
   * committed section) does not silently re-add it — see {@link commitContent}.
   */
  deleteSection: (section: string) => void;
  /** Commit the current content as a new version (resolves to ok|conflict|error). */
  commit: (note: string, providerApproved: boolean) => Promise<CommitResult>;
  /**
   * Load a specific version's content into the editor (any status, including
   * an AI-`proposed` version), so the provider can open it, edit it, and
   * commit it as a new version. Resolves true on success.
   */
  loadVersionContent: (versionNo: number) => Promise<boolean>;
  /**
   * Restore a prior version as the new current version in one step: the backend
   * commits that version's full content verbatim (preserving citations) as a new
   * committed version and this reloads. Resolves `"approval"` when the backend
   * schema-lock gate (422) requires provider approval for a section-reshaping
   * Blueprint restore — the caller obtains approval and retries with
   * `providerApproved = true`.
   */
  restore: (
    versionNo: number,
    providerApproved: boolean,
  ) => Promise<RestoreResult>;
  /** Fetch the section diff between two version numbers. */
  loadDiff: (fromNo: number, toNo: number) => Promise<VersionDiff | null>;
  /**
   * Preview the diff a commit of `content` would produce against the current
   * committed version, WITHOUT committing anything (BLA-362/B1's
   * save-version review dialog). Resolves `null` on any failure — the caller
   * degrades honestly rather than showing a stale/wrong diff.
   */
  loadDiffPreview: (
    content: Record<string, string>,
  ) => Promise<VersionDiff | null>;
  /**
   * Request a concise AI-drafted "what changed" summary for a would-be commit
   * (BLA-362/B3). **Fail open + quiet**: any failure (network, provider,
   * timeout) resolves to `""`, never rejects — callers must treat an empty
   * string as the normal "nothing to prefill" outcome, not an error.
   */
  loadChangeSummary: (content: Record<string, string>) => Promise<string>;
  /**
   * Re-run the full load (e.g. after an external change). For the *current*
   * document this is a same-document refresh — see {@link isRefreshing} — not
   * a full reset; it never blanks the already-mounted editor.
   */
  reload: () => void;
}

/** The base committed-version number a draft/commit is based on (0 when none). */
function baseVersionOf(version: BlueprintVersion | null): number {
  return version?.version_no ?? 0;
}

/**
 * The default editable section for a brand-new, unversioned document.
 *
 * A freshly-created document has no committed version and no draft, so its
 * content map is empty — which would render zero section editors, leaving
 * nothing to type into. Seed a single free-form `body` section so the editor
 * surface is always present and a first version can be committed.
 */
export const DEFAULT_SECTION_KEY = "body";

/**
 * Resolve the initial editor content when a document has no version or draft.
 *
 * @returns A content map with one empty, free-form `body` section.
 */
function emptyDocumentContent(): Record<string, string> {
  return { [DEFAULT_SECTION_KEY]: "" };
}

/**
 * Coerce one section value into the markdown string the editor renders.
 *
 * The version-content API is typed as `Record<string, string>`, but a stored
 * version can carry a non-string section value — a structured `metadata` block,
 * a `null`, or content authored under an earlier shape — that the type does not
 * actually guarantee. The editor (`textarea.value`, `applyFormat`, the markdown
 * preview's `source.trim()`) assumes a string and throws when handed anything
 * else, which is what crashed the app when an older version was restored. Coerce
 * defensively: pass strings through, drop `null`/`undefined` to `""`, and
 * JSON-stringify any other value so it is editable rather than fatal.
 *
 * @param value - The raw section value from a loaded version.
 * @returns A markdown string safe to load into the editor.
 */
function coerceSectionValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  return JSON.stringify(value, null, 2);
}

/**
 * Normalize a loaded version's content so every section value is a string.
 *
 * @param content - The raw section-keyed content from a version (each value may
 * be malformed despite the `Record<string, string>` type).
 * @returns A content map with every value coerced to a markdown string.
 */
function normalizeContent(
  content: Record<string, string>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(content).map(([key, value]) => [
      key,
      coerceSectionValue(value),
    ]),
  );
}

/**
 * Overlay the live editor content onto the committed version's content.
 *
 * The editor only surfaces editable section cards; a committed version may
 * carry additional sections it does not edit (notably the blueprint
 * `metadata` block, shown in the MetadataBar). Committing only the editable
 * cards would drop those sections, changing the committed section *set* and
 * tripping the schema-lock provider-approval gate for an ordinary content
 * edit. Overlaying preserves every committed section while letting edited
 * sections win.
 *
 * @param committed - The committed version's content (section-keyed).
 * @param edited - The live editor content (section-keyed).
 * @returns The merged content a commit should persist.
 */
function overlayContent(
  committed: Record<string, string>,
  edited: Record<string, string>,
): Record<string, string> {
  return { ...committed, ...edited };
}

/**
 * Return a copy of a content map with the given section keys removed.
 *
 * The commit overlay (`{...committed, ...edited}`) restores every committed
 * section so non-card sections (e.g. blueprint `metadata`) survive a content
 * edit. That same restore would silently re-add a section the provider just
 * deleted — so the deleted keys must be subtracted *after* the overlay, in both
 * the commit payload and the exposed {@link UseDocumentVersionsResult.commitContent}.
 *
 * @param source - The content map to filter (section-keyed).
 * @param keys - The section keys to omit.
 * @returns A new content map without the omitted keys.
 */
function omit(
  source: Record<string, string>,
  keys: ReadonlySet<string>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(source).filter(([key]) => !keys.has(key)),
  );
}

/**
 * Apply the commit overlay then subtract any deleted section keys.
 *
 * @param committed - The committed version's content (section-keyed).
 * @param edited - The live editor content (section-keyed).
 * @param deleted - The section keys the provider has deleted.
 * @returns The content a commit should persist (overlay minus deletions).
 */
function resolveCommitContent(
  committed: Record<string, string>,
  edited: Record<string, string>,
  deleted: ReadonlySet<string>,
): Record<string, string> {
  return omit(overlayContent(committed, edited), deleted);
}

/**
 * Manage a document's load, edit, autosave, commit, and diff lifecycle.
 *
 * @param documentId - The document to edit (empty string disables the hook).
 * @returns The document editing state plus edit/commit/diff/reload actions.
 */
export function useDocumentVersions(
  documentId: string,
): UseDocumentVersionsResult {
  const [status, setStatus] = useState<DocumentStatus>("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [document, setDocument] = useState<BlueprintDocument | null>(null);
  const [version, setVersion] = useState<BlueprintVersion | null>(null);
  const [hasCardProvenance, setHasCardProvenance] = useState(false);
  const [sourceTitles, setSourceTitles] = useState<Record<string, string>>({});
  const [cardTitles, setCardTitles] = useState<Record<string, string>>({});
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [content, setContent] = useState<Record<string, string>>({});
  const [deletedSections, setDeletedSections] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [conflict, setConflict] = useState(false);
  const [draftConflict, setDraftConflict] = useState(false);
  const [draftBaseVersion, setDraftBaseVersion] = useState<number | null>(null);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const versionRef = useRef<BlueprintVersion | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Uniquely claimed by each loadVersionContent() call (incremented at its
  // start), so a call can tell whether a NEWER loadVersionContent call has
  // since superseded it — two overlapping calls (e.g. clicking "load
  // version 3" then "load version 4" before the first resolves) are an
  // out-of-order-response race: applying a stale response would otherwise
  // revert the display backward to content the caller already navigated
  // away from.
  const loadVersionCallTokenRef = useRef<number>(0);
  // The document id whose initial load has already completed. A `load()` run
  // for the SAME id (triggered by `reload()` after a commit/restore) is a
  // refresh, not a document switch — it must not blank the mounted editor
  // (dropdown/comment-sidebar/scroll state) by cycling status back through
  // "loading". A run for a DIFFERENT id (or the very first run) is a genuine
  // switch/initial-mount and must still show the full loading state so stale
  // prior-document content is never shown.
  const loadedDocumentIdRef = useRef<string | null>(null);

  useEffect(() => {
    versionRef.current = version;
  }, [version]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (documentId === "") return undefined;
    let active = true;
    const isSameDocumentRefresh = loadedDocumentIdRef.current === documentId;

    async function load(): Promise<void> {
      if (isSameDocumentRefresh) {
        setIsRefreshing(true);
        setStatus("loaded");
      } else {
        setStatus("loading");
        setIsRefreshing(false);
      }
      // BLA-348: "render a document" client wall-clock (network + server +
      // parse), grouped by document type so a source's render isn't averaged
      // together with a markdown/entity card's render.
      const detail = await timeOperation(
        "documents.render_fetch",
        () =>
          apiUtils.fetchData<BlueprintDocumentResponse>(
            `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}`,
          ),
        // `fetchData` RETURNS an ApiError rather than throwing, so
        // `timeOperation`'s own catch branch never sees an HTTP failure —
        // tag the outcome here so a failed fetch can be excluded from the
        // success percentile (see `useDocumentLibrary.ts`).
        (result) => ({
          outcome: isApiError(result) ? "error" : "ok",
          doc_type: isApiError(result) ? "unknown" : result.document.type,
        }),
      );
      if (!active) return;
      if (isApiError(detail)) {
        setStatus("error");
        setIsRefreshing(false);
        return;
      }
      const list = await apiUtils.fetchData<VersionSummary[]>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}/versions`,
      );
      const draft = await apiUtils.fetchData<DocumentDraft | null>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}/draft`,
      );
      if (!active) return;
      setDocument(detail.document);
      setVersion(detail.current_version);
      setHasCardProvenance(detail.has_card_provenance ?? false);
      setSourceTitles(detail.source_titles ?? {});
      setCardTitles(detail.card_titles ?? {});
      setVersions(isApiError(list) ? [] : list);
      const committed = detail.current_version?.content;
      // ZERO-WIPED-DRAFTS POLICY:
      // An author's in-progress or autosaved draft must NEVER be silently discarded,
      // even if the server's committed version has advanced since the draft was saved.
      // If a draft exists, preserve draft.content into the editor. If the draft's
      // base_version differs from current committed version, flag draftConflict so the
      // clinician can resolve/fork without losing work.
      const currentVerNo = baseVersionOf(detail.current_version);
      const hasAnyDraft = !isApiError(draft) && draft !== null;
      const isDraftStale = hasAnyDraft && draft.base_version !== currentVerNo;

      if (hasAnyDraft && isDraftStale) {
        setDraftConflict(true);
        setDraftBaseVersion(draft.base_version);
      } else {
        setDraftConflict(false);
        setDraftBaseVersion(null);
      }

      // No committed version and no draft → seed a default editable section so
      // a brand-new document is immediately editable (otherwise zero editors
      // render and there is nothing to type into).
      const baseline =
        committed && Object.keys(committed).length > 0
          ? committed
          : emptyDocumentContent();
      // Coerce every section value to a string before it reaches the editor —
      // a malformed stored value (non-string/null) otherwise crashes the
      // markdown editor/preview (see normalizeContent).
      setContent(normalizeContent(hasAnyDraft ? draft.content : baseline));
      // A fresh load (initial mount, reload, or post-commit) starts from the
      // newly committed/baseline section set, so any prior in-session deletions
      // are resolved — clear them lest they leak into the next commit's overlay.
      setDeletedSections(new Set());
      setSaveState(hasAnyDraft ? "saved" : "idle");
      setConflict(false);
      setCommitError(null);
      loadedDocumentIdRef.current = documentId;
      setStatus("loaded");
      setIsRefreshing(false);
    }

    load().catch((err: unknown) => {
      if (!active) return;
      logger.devOnly.warn("useDocumentVersions: load failed", {
        error: String(err),
      });
      setStatus("error");
      setIsRefreshing(false);
    });

    return () => {
      active = false;
    };
  }, [documentId, reloadToken]);

  // Cancel a pending debounced autosave without firing it. A commit/restore/
  // version-load makes any not-yet-fired autosave's snapshot obsolete before
  // it can ever be sent — letting it fire anyway would only waste a request
  // and flip saveState to a misleading "saved" for content that reload is
  // about to discard. Called only on success (never on failure/conflict):
  // nothing changed then, so a pending autosave is still valid and must be
  // left to fire.
  const cancelPendingAutosave = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const autosave = useCallback(
    async (
      next: Record<string, string>,
      baseVersion: number,
    ): Promise<void> => {
      setSaveState("saving");
      const resp = await apiUtils.fetchData<DocumentDraft>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}/draft`,
        "PUT",
        { content: next, base_version: baseVersion },
      );
      setSaveState(isApiError(resp) ? "unsaved" : "saved");
    },
    [documentId],
  );

  const scheduleAutosave = useCallback(
    (next: Record<string, string>): void => {
      setSaveState("unsaved");
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      // Capture the version this edit is based on NOW, at schedule time —
      // not whatever happens to be current whenever the debounce later
      // fires. A commit/restore between scheduling and firing advances the
      // document past this snapshot; the draft PUT must say so honestly (see
      // load()'s freshness check above) instead of silently relabeling stale
      // content as based on the new version.
      const baseVersion = baseVersionOf(versionRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void autosave(next, baseVersion);
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [autosave],
  );

  const editSection = useCallback(
    (section: string, markdown: string): void => {
      setContent((prev) => {
        const next = { ...prev, [section]: markdown };
        scheduleAutosave(next);
        return next;
      });
    },
    [scheduleAutosave],
  );

  const deleteSection = useCallback(
    (section: string): void => {
      // Record the deletion so the commit overlay does not resurrect the key
      // from the committed version (the overlay-resurrection guard).
      setDeletedSections((prev) => new Set(prev).add(section));
      setContent((prev) => {
        const next = omit(prev, new Set([section]));
        scheduleAutosave(next);
        return next;
      });
    },
    [scheduleAutosave],
  );

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  const commit = useCallback(
    async (note: string, providerApproved: boolean): Promise<CommitResult> => {
      setCommitError(null);
      // BLA-348: "save a document version". A stale-`base_version` 409 is
      // routine control flow here, so the outcome is tagged — an expected
      // rejection must not land in the success percentile.
      const resp = await timeOperation(
        "documents.commit_version",
        () =>
          apiUtils.fetchData<BlueprintVersion>(
            `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}/commit`,
            "POST",
            {
              content: resolveCommitContent(
                versionRef.current?.content ?? {},
                content,
                deletedSections,
              ),
              base_version: baseVersionOf(versionRef.current),
              note: note.trim() === "" ? null : note.trim(),
              provider_approved: providerApproved,
            },
          ),
        (result) => ({ outcome: isApiError(result) ? "error" : "ok" }),
      );
      if (isApiError(resp)) {
        if (resp.status === 409) {
          setConflict(true);
          return "conflict";
        }
        // Any other failure (e.g. a 422 schema-lock rejection) must surface to
        // the provider rather than fail silently.
        setCommitError(resp.error);
        return "error";
      }
      // A pending autosave scheduled before this commit succeeded is now
      // guaranteed stale (its snapshot predates the version just committed)
      // — cancel it rather than let it fire later into a document that has
      // moved on. Never reached on a conflict/error: nothing changed then,
      // so any pending autosave is still valid and must be left to fire.
      cancelPendingAutosave();
      setVersion(resp);
      setConflict(false);
      setDraftConflict(false);
      setDraftBaseVersion(null);
      setSaveState("idle");
      reload();
      return "ok";
    },
    [documentId, content, deletedSections, reload, cancelPendingAutosave],
  );

  const loadVersionContent = useCallback(
    async (versionNo: number): Promise<boolean> => {
      // Claim a unique token for THIS call so it can tell whether a NEWER
      // loadVersionContent call has since superseded it — two overlapping
      // calls (e.g. clicking "load version 3" then "load version 4" before
      // the first resolves) are an out-of-order-response race: applying a
      // stale response would otherwise revert the display backward to
      // content the caller already navigated away from. This is independent
      // of autosave/draft handling below — it is purely about which
      // response is allowed to win.
      const myToken = ++loadVersionCallTokenRef.current;
      const resp = await apiUtils.fetchData<BlueprintVersion>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}` +
          `/versions/${versionNo}`,
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useDocumentVersions: load version failed");
        return false;
      }
      if (loadVersionCallTokenRef.current !== myToken) {
        // A newer loadVersionContent call has since claimed the working
        // content (and already applied its own, more current response).
        return false;
      }
      // A pending autosave scheduled against the content this call is about
      // to replace is superseded now that the load succeeded — cancel it
      // (on success only, same as commit()/restore()) rather than let it
      // fire later and overwrite the just-loaded working set with what it
      // captured before this call. Not restored on failure: a failed load
      // never touches `content` above, so the user's actual in-memory edit
      // is untouched either way — only the debounced *server-side* draft
      // save-in-progress is lost, and the next edit or an explicit commit
      // naturally re-establishes it.
      cancelPendingAutosave();
      // Load this version's content into the editor as the working set. A
      // subsequent commit overlays it onto the *current committed* content
      // (via versionRef in commit), so submitting an AI-`proposed` version
      // commits/accepts it as a new committed version while preserving the
      // committed section set (e.g. the blueprint `metadata` block).
      // Normalize first: an older/proposed version can carry a non-string
      // section value that would crash the editor on restore (see
      // normalizeContent) — coerce to a safe, editable string instead.
      setContent(normalizeContent(resp.content));
      // The loaded version's content is the new working set, superseding any
      // in-session deletions tracked against the previously-loaded content.
      setDeletedSections(new Set());
      setSaveState("unsaved");
      return true;
    },
    [documentId, cancelPendingAutosave],
  );

  const restore = useCallback(
    async (
      versionNo: number,
      providerApproved: boolean,
    ): Promise<RestoreResult> => {
      // BLA-348: "restore a document version". A schema-lock 422 is routine
      // control flow here, so the outcome is tagged — an expected rejection
      // must not land in the success percentile.
      const resp = await timeOperation(
        "documents.restore_version",
        () =>
          apiUtils.fetchData<BlueprintVersion>(
            `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}` +
              `/versions/${versionNo}/restore`,
            "POST",
            { provider_approved: providerApproved },
          ),
        (result) => ({ outcome: isApiError(result) ? "error" : "ok" }),
      );
      if (isApiError(resp)) {
        // A 422 is the schema-lock gate refusing a section-reshaping Blueprint
        // restore without approval — recoverable by retrying with approval.
        if (resp.status === 422) return "approval";
        logger.devOnly.warn("useDocumentVersions: restore failed");
        return "error";
      }
      // A pending autosave scheduled before this restore succeeded is now
      // guaranteed stale (BLA-329) — cancel it so it can never fire a PUT
      // that pairs its pre-restore content with the just-committed version
      // number. Never reached on a 422/error: nothing committed then, so any
      // pending autosave is still valid and must be left to fire.
      cancelPendingAutosave();
      // The restore committed a new current version; reload so the editor
      // remounts on the fresh committed content (this is also what makes the
      // restored content visible, independent of the Rich-editor sync fix).
      setVersion(resp);
      setConflict(false);
      setSaveState("idle");
      reload();
      return "ok";
    },
    [documentId, reload, cancelPendingAutosave],
  );

  const loadDiff = useCallback(
    async (fromNo: number, toNo: number): Promise<VersionDiff | null> => {
      const resp = await apiUtils.fetchData<VersionDiff>(
        `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}/diff` +
          `?from_version_no=${fromNo}&to_version_no=${toNo}`,
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useDocumentVersions: diff failed");
        return null;
      }
      return resp;
    },
    [documentId],
  );

  const loadDiffPreview = useCallback(
    async (
      previewContent: Record<string, string>,
    ): Promise<VersionDiff | null> => {
      // BLA-348/B4: times the client-visible "open the save-review dialog"
      // wait — the diff must show immediately (B1), so this is on the
      // critical path, unlike the AI summary (loadChangeSummary), which is
      // deliberately NOT timed as part of this op (it is not on that path).
      const resp = await timeOperation(
        "documents.save_review_diff",
        () =>
          apiUtils.fetchData<VersionDiff>(
            `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}/diff-preview`,
            "POST",
            { content: previewContent },
          ),
        (result) => ({ outcome: isApiError(result) ? "error" : "ok" }),
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useDocumentVersions: diff preview failed");
        return null;
      }
      return resp;
    },
    [documentId],
  );

  const loadChangeSummary = useCallback(
    async (previewContent: Record<string, string>): Promise<string> => {
      // Fail open + quiet (B3): apiUtils.fetchData never throws for an HTTP
      // failure (it returns an ApiError), so the only way this could ever
      // reject is an unexpected client-side error (e.g. a bug) — caught here
      // too, since a broken "nice to have" prefill must never surface as a
      // save-blocking error.
      try {
        const resp = await apiUtils.fetchData<{ summary: string }>(
          `${DOCUMENTS_ENDPOINT}/${encodeURIComponent(documentId)}/summarize-changes`,
          "POST",
          { content: previewContent },
        );
        return isApiError(resp) ? "" : resp.summary;
      } catch {
        return "";
      }
    },
    [documentId],
  );

  return {
    status,
    isRefreshing,
    document,
    version,
    hasCardProvenance,
    sourceTitles,
    cardTitles,
    versions,
    content,
    commitContent: resolveCommitContent(
      version?.content ?? {},
      content,
      deletedSections,
    ),
    saveState,
    conflict,
    draftConflict,
    draftBaseVersion,
    commitError,
    editSection,
    deleteSection,
    commit,
    loadVersionContent,
    restore,
    loadDiff,
    loadDiffPreview,
    loadChangeSummary,
    reload,
  };
}
